// packages/api-client/src/services/admin/adminUserService.ts
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { ServiceResponse, UserDB, AdminAddCreditsPayload, AdminUserActionResponse } from '@dopomogai/types';

const ADMIN_USERS_PREFIX = '/admin/users/';

export interface UserListFilters {
  searchQuery?: string;
  role?: string;
}

export const adminUserService = {
  /**
   * Fetches a list of all users. Admin-only.
   * Now supports optional filters for searching and role-based filtering.
   * @param filters - An object containing the filters to apply.
   */
  listAllUsers: (filters?: UserListFilters): ServiceResponse<UserDB[]> => {
    return getApiClient().get<UserDB[]>(ADMIN_USERS_PREFIX, { params: filters })
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing all users'));
  },

  /**
   * Manually adds credits to a user's account.
   */
  addCreditsToUser: (userId: string, payload: AdminAddCreditsPayload): ServiceResponse<AdminUserActionResponse> => {
    const url = `${ADMIN_USERS_PREFIX}${userId}/add-credits`;
    return getApiClient().post<AdminUserActionResponse>(url, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `adding credits to user ${userId}`));
  },

  /**
   * Blocks a user from accessing the platform.
   */
  blockUser: (userId: string): ServiceResponse<AdminUserActionResponse> => {
    const url = `${ADMIN_USERS_PREFIX}${userId}/block`;
    return getApiClient().post<AdminUserActionResponse>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, `blocking user ${userId}`));
  },

  /**
   * Unblocks a user.
   */
  unblockUser: (userId: string): ServiceResponse<AdminUserActionResponse> => {
    const url = `${ADMIN_USERS_PREFIX}${userId}/unblock`;
    return getApiClient().post<AdminUserActionResponse>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, `unblocking user ${userId}`));
  },

  /**
   * Permanently deletes a user from the system.
   */
  deleteUser: (userId: string): ServiceResponse<void> => {
    const url = `${ADMIN_USERS_PREFIX}${userId}`;
    return getApiClient().delete<void>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, `deleting user ${userId}`));
  },
};